"use client";

import React from "react";
import { CircleCheck, Truck, Wrench, PackageCheck, Zap, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const PRICING_FEATURES = ["Diagnóstico profesional y preciso", "Mano de obra técnica especializada", "Test de capacidad y rendimiento", "Revisión de sistema BMS"];
const SHIPPING_STEPS = [{
  icon: Truck,
  title: "Paso 1: Contacta desde Sevilla, Madrid o Barcelona para el diagnóstico.",
  description: "Te asesoramos sobre el embalaje seguro para el transporte nacional de tu batería."
}, {
  icon: Wrench,
  title: "Paso 2: Recogida en tu domicilio mediante mensajería especializada.",
  description: "Gestionamos el envío seguro desde cualquier punto de España (Madrid, Barcelona, Valencia) a nuestro taller."
}, {
  icon: PackageCheck,
  title: "Paso 3: Reparación técnica y devolución directa a tu ciudad.",
  description: "Recibe tu batería reparada y testada con garantía total en tu domicilio."
}];
export default function TarifasTransparentesYEnviosATodaEspana() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: processRef,
    isVisible: isProcessVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="tarifas-transparentes-y-envíos-a-toda-españa" className="bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Tarifas Transparentes y <br className="hidden md:block" />
            <span className="text-primary">Envíos a Toda España</span>
          </h1>
          <p className="text-lg text-muted-foreground">
            Ofrecemos un servicio técnico especializado en Sevilla, Mairena del Aljarafe y todo el Aljarafe, con cobertura nacional en ciudades como Madrid, Barcelona y Valencia. 
            Precios claros desde el primer momento, sin sorpresas.
          </p>
        </div>

        {}
        <div ref={cardsRef} className={`grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12 transition-all duration-700 delay-200 ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <Card className="flex flex-col border-border/50 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="text-center pb-8">
              <div className="mx-auto w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-secondary-foreground" />
              </div>
              <CardTitle className="text-2xl font-default mb-2">Local Sevilla</CardTitle>
              <CardDescription>
                Entrega y recogida directa en nuestro taller especializado en Sevilla, sirviendo también a Mairena del Aljarafe y la zona del Aljarafe. El precio incluye diagnóstico completo y mano de obra técnica.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="text-center mb-8">
                <span className="text-5xl font-bold font-default text-foreground">20€</span>
                <span className="text-muted-foreground ml-2">/ desde</span>
              </div>
              <ul className="space-y-4">
                {PRICING_FEATURES.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-foreground/80 text-sm">{feature}</span>
                  </li>)}
              </ul>
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline" className="w-full h-12 text-base">
                <Link to="https://wa.me/34624990509" newTab>Solicitar Cita en Taller</Link>
              </Button>
            </CardFooter>
          </Card>

          {}
          <Card className="flex flex-col border-2 border-primary shadow-lg shadow-primary/10 relative overflow-hidden">
            {}
            <div className="absolute top-5 right-[-35px] rotate-45 bg-primary text-primary-foreground text-xs font-bold py-1 w-[150px] text-center shadow-sm">
              MÁS POPULAR
            </div>
            
            <CardHeader className="text-center pb-8 pt-10">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-2xl font-default mb-2">Nacional (España)</CardTitle>
              <CardDescription>
                Servicio puerta a puerta para Madrid, Barcelona, Mairena del Aljarafe, el Aljarafe y toda la península. Incluye gestión de recogida, transporte asegurado y diagnóstico.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="text-center mb-8">
                <span className="text-5xl font-bold font-default text-foreground">20€</span>
                <span className="text-muted-foreground ml-2">+ envío</span>
              </div>
              <ul className="space-y-4">
                {PRICING_FEATURES.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-foreground/80 text-sm">{feature}</span>
                  </li>)}
                <li data-index="extra" className="flex items-start gap-3 pt-2 border-t border-border/50">
                  <Truck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium text-sm">Gestión integral de transporte ida y vuelta</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button asChild className="w-full h-12 text-base group">
                <Link to="https://wa.me/34624990509" newTab>
                  Gestionar Envío
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>

        {}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <p className="text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg border border-border/50 inline-block">
            <strong className="text-foreground font-medium">* Importante:</strong> El precio base indicado cubre el diagnóstico y la mano de obra inicial. 
            Los materiales específicos (celdas de litio, placas BMS, conectores) necesarios para la reparación o reconstrucción 
            se presupuestarán de forma transparente y separada tras la evaluación técnica.
          </p>
        </div>

        {}
        <div ref={processRef} className={`max-w-5xl mx-auto transition-all duration-700 delay-400 ${isProcessVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12">
            <h4 className="text-2xl font-bold font-default text-foreground mb-4">
              ¿Cómo enviar tu batería para reparar desde cualquier punto de España, incluyendo Mairena del Aljarafe y el Aljarafe? Servicio técnico de baterías en España.
            </h4>
            <p className="text-muted-foreground">
              Si no estás en Sevilla, Mairena del Aljarafe o el Aljarafe, puedes enviarnos tu batería de forma fácil y segura. Nuestro servicio técnico de baterías en España facilita el envío de batería para reparación y el proceso para reparar tu batería de patinete en España. Trabajamos con clientes de Madrid, Barcelona, Valencia y toda España ofreciendo un servicio rápido y profesional.
            </p>
          </div>

          <div className="relative mt-12 px-4 md:px-0">
            {}
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] z-0">
              <Progress value={100} className="h-1 bg-muted" />
            </div>

            <div className="relative z-10 flex flex-col md:flex-row justify-between gap-8 md:gap-4">
              {SHIPPING_STEPS.map((step, i) => {
              const Icon = step.icon;
              return <div key={i} data-index={i} className="flex flex-col items-center text-center md:w-1/3">
                    <div className="w-16 h-16 rounded-full bg-background border-4 border-background shadow-[0_0_0_2px_hsl(var(--primary))] flex items-center justify-center mb-6 relative z-10">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h5 className="text-lg font-bold font-default text-foreground mb-2">
                      {step.title}
                    </h5>
                    <p className="text-sm text-muted-foreground max-w-[250px] mx-auto">
                      {step.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </div>

        <div className="text-center mt-16">
          <p className="text-foreground font-medium mb-6">
            Servicio disponible en toda España, Sevilla, Mairena del Aljarafe y el Aljarafe con asesoramiento en todo momento.
          </p>
          <Button asChild size="lg">
            <Link to="https://wa.me/34624990509?text=Hola,%20quiero%20informaci%C3%B3n%20sobre%20la%20reparaci%C3%B3n%20de%20bater%C3%ADas." newTab>Solicitar diagnóstico</Link>
          </Button>
        </div>
      </div>
    </section>;
}